import { BrandLocationData } from '@components/Dashboard/CampaignAnalytics/slice/schema';
import { buildChartOptions } from '@configurations/campaignAnalytics/brandLocationVisitsChart';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useMemo, ReactElement } from 'react';
import { ChartCard } from '../ChartCard';
import { EditableChartProps } from './chartProps';
import LocationTable, { LocationTableProps } from '../LocationTable';
import { LocationListEditor } from '@components/CampaignAnalytics/LocationListEditor';
import { MISSING_VALUE } from '@constants/stats';

type BrandLocationVisitsProps = EditableChartProps;

const LOCATIONS_COUNT = 20;

type TableColumn = keyof BrandLocationData | 'rank';

type ColumnData<T> = {
  column: TableColumn;
  label: string;
  editOnly?: boolean;
  renderCell?: (props: T) => ReactElement;
};

const ORDERED_COLUMNS: ColumnData<BrandLocationData>[] = [
  { column: 'rank', label: 'Rank' },
  { column: 'name', label: 'Name' },
  { column: 'address', label: 'Address' },
  { column: 'city', label: 'City' },
  { column: 'state', label: 'State' },
  { column: 'zip', label: 'Zip' },
  { column: 'index', label: 'Index' },
  {
    column: 'locationListExclusionId',
    label: 'Include in Report',
    editOnly: true,
    renderCell: locationData => (
      <LocationListEditor
        locationListExclusionId={locationData.locationListExclusionId}
        locationId={locationData.locationId}
        locationListId={locationData.locationListId}
      />
    ),
  },
];

const getRank = (
  brandLocationData: BrandLocationData,
  filteredTopBrandLocations: BrandLocationData[]
) => {
  if (brandLocationData.locationListExclusionId) return MISSING_VALUE;

  const index = filteredTopBrandLocations.findIndex(
    location =>
      location.address === brandLocationData.address &&
      location.city === brandLocationData.city &&
      location.state === brandLocationData.state &&
      location.zip === brandLocationData.zip
  );

  return index === -1 ? MISSING_VALUE : index + 1;
};

const buildTableData = (
  locationData: BrandLocationData[],
  showEditColumns: boolean,
  filteredTopBrandLocations: BrandLocationData[]
): LocationTableProps['tableData'] => {
  const filteredColumns = ORDERED_COLUMNS.filter(columnData =>
    showEditColumns ? true : !columnData.editOnly
  );
  return [
    filteredColumns.map(columnData => ({
      key: columnData.column,
      value: columnData.label,
    })),
    ...locationData.map(brandLocation =>
      filteredColumns.map(columnData => ({
        key: columnData.column,
        value:
          columnData.column === 'rank'
            ? getRank(brandLocation, filteredTopBrandLocations)
            : brandLocation[columnData.column],
        locationData: brandLocation,
        renderCell: columnData.renderCell,
      }))
    ),
  ];
};

const filterTopBrandLocations = (topBrandLocations: BrandLocationData[]) => {
  return topBrandLocations
    .filter(location => !location.locationListExclusionId)
    .sort((a, b) => b.index - a.index);
};

const BrandLocationVisits = (props: BrandLocationVisitsProps) => {
  const { report, ...chartCardProps } = props;

  const filteredTopBrandLocations = useMemo(
    () =>
      report ? filterTopBrandLocations(report.brandLocationVisits.data) : [],
    [report]
  );
  const baseBrandLocations = chartCardProps.editable
    ? report.brandLocationVisits.data.toSorted((a, b) => b.index - a.index)
    : filteredTopBrandLocations;

  const memoizedChartOptions = useMemo(
    () => buildChartOptions(filteredTopBrandLocations),
    [filteredTopBrandLocations]
  );

  if (!report) return null;

  const includedLocations = filteredTopBrandLocations.slice(0, LOCATIONS_COUNT);

  const lastDisplayableIndex =
    baseBrandLocations.findIndex(location => {
      const lastIncludedLocation =
        // eslint-disable-next-line unicorn/prefer-at
        includedLocations[includedLocations.length - 1];
      return (
        lastIncludedLocation &&
        location.locationId === lastIncludedLocation.locationId &&
        location.locationListId === lastIncludedLocation.locationListId &&
        location.address === lastIncludedLocation.address
      );
      // eslint-disable-next-line unicorn/explicit-length-check
    }) + 1 || baseBrandLocations.length;

  return (
    <ChartCard
      title="Top 20 Brand Locations Visited"
      description={`${props.campaignName} (${props.campaignId})`}
      report={report.brandLocationVisits}
      {...chartCardProps}
    >
      <>
        <HighchartsReact
          constructorType="mapChart"
          highcharts={Highcharts}
          options={memoizedChartOptions}
        />
        <LocationTable
          tableData={buildTableData(
            baseBrandLocations.slice(0, lastDisplayableIndex),
            !!chartCardProps.editable,
            includedLocations
          )}
        />
      </>
    </ChartCard>
  );
};

export default BrandLocationVisits;
